import { Directive, HostListener, ElementRef, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';
import { parseMonetary } from '../services/price-parser';

/**
 * Guards price inputs against pasted values.
 *
 * A native <input type="number"> rejects invalid *keystrokes* but not pasted
 * text: pasting "—" leaves the control empty, Angular writes null/NaN into the
 * model, and downstream `Number(x) || 0` turns it into 0. That is how an em
 * dash became a R0 purchase price.
 *
 * This runs pasted text through the same parseMonetary used by the Excel
 * import, so a value behaves identically however it arrives.
 */
@Directive({
  selector: '[appNumericInput]',
  standalone: true
})
export class NumericInputDirective {
  constructor(
    private el: ElementRef<HTMLInputElement>,
    @Optional() private control: NgControl
  ) {}

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text');
    if (pasted === undefined || pasted === null) {
      return;
    }

    // Always take over: letting the browser handle it is what silently blanks
    // the field on invalid input.
    event.preventDefault();

    const { value, error } = parseMonetary(pasted);

    if (error) {
      this.setValue(null);
      this.control?.control?.setErrors({
        ...(this.control.control.errors || {}),
        invalidPrice: error
      });
      this.control?.control?.markAsTouched();
      return;
    }

    this.setValue(value);
    // Clear a previous paste error now that the value is good.
    const errors = { ...(this.control?.control?.errors || {}) } as any;
    if (errors['invalidPrice']) {
      delete errors['invalidPrice'];
      this.control?.control?.setErrors(Object.keys(errors).length ? errors : null);
    }
  }

  /** Scroll wheel silently mutates a focused number input; prevent it. */
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    if (document.activeElement === this.el.nativeElement) {
      event.preventDefault();
    }
  }

  private setValue(value: number | null): void {
    const text = value === null ? '' : String(value);
    this.el.nativeElement.value = text;

    if (this.control?.control) {
      this.control.control.setValue(value, { emitEvent: true });
      this.control.control.markAsDirty();
    } else {
      // ngModel-bound inputs need a synthetic input event to update the model.
      this.el.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}
